import { createEntityAdapter, createSlice, isAnyOf } from "@reduxjs/toolkit";
import sortBy from "lodash/sortBy";

import { KanbanAxios } from "../../axios/kanban/kanban.axios";
import { Kanban } from "../../model/kanban/Kanban";
import { KanbanAutomationRule } from "../../model/kanban/KanbanAutomationRule";
import { KanbanCard } from "../../model/kanban/KanbanCard";
import { KanbanColumn } from "../../model/kanban/KanbanColumn";
import { CreateKanbanColumnDto } from "../../model/kanban/dto/CreateKanabnColumnDto";
import { CreateKanbanAutomationRuleDto } from "../../model/kanban/dto/CreateKanbanAutomationRuleDto";
import { CreateKanbanCardDto } from "../../model/kanban/dto/CreateKanbanCardDto";
import { CreateKanbanDto } from "../../model/kanban/dto/CreateKanbanDto";
import { DeleteKanbanAutomationRuleDto } from "../../model/kanban/dto/DeleteKanbanAutomationRuleDto";
import { GetKanbanCardByIdDto } from "../../model/kanban/dto/GetKanbanCardByIdDto";
import { GetKanbanCardsByKanbanColumnIdDto } from "../../model/kanban/dto/GetKanbanCardsByKanbanColumnIdDto";
import { GetUserKanbanDto } from "../../model/kanban/dto/GetUserDefaultKanbanDto";
import { GetUserKanbansMetaDto } from "../../model/kanban/dto/GetUserDefaultKanbansMetaDto";
import { GetUserKanbansMetaResponseDto } from "../../model/kanban/dto/GetUserDefaultKanbansMetaResponseDto";
import { UpdateKanbanAutomationRuleDto } from "../../model/kanban/dto/UpdateKanbanAutomationRuleDto";
import { UpdateKanbanCardDto } from "../../model/kanban/dto/UpdateKanbanCardDto";
import { UpdateKanbanColumnDto } from "../../model/kanban/dto/UpdateKanbanColumnDto";
import { UpdateKanbanDto } from "../../model/kanban/dto/UpdateKanbanDto";
import { UpdateKanbanClosingReasonsDto } from "../../model/kanban/dto/UpdateKanbanClosingReasonsDto";
import { PageResponseDto } from "../../model/meta/PageResponseDto";
import { AtiraThunk } from "../AtiraThunk";
import { userActions } from "../user/user.slice";
import { UpdateKanbanCardBetweenKanbansDto } from "./../../model/kanban/dto/UpdateKanbanCardBetweenKanbansDto";
import { KanbanCardReduxEntry } from "./kanbanCard.reduxy-entry";
import { revertAllReduxAction } from "../store-reset";
import { DeleteKanbanDto } from "../../model/kanban/dto/DeleteKanbanDto";
import { DeleteKanbanCardDto } from "../../model/kanban/dto/DeleteKanbanCardDto";
import { ExportKanbanCardsDto } from "../../model/kanban/dto/ExportKanbanCardsDto";

const updateCardInColumn = (column: KanbanColumn, updatedCard: KanbanCard) => {
  return {
    ...column,
    cards: column.cards?.map((card) =>
      card._id === updatedCard._id ? updatedCard : card,
    ),
  };
};

// Function to update the kanban with the new column
const _updateKanban = (kanban: Kanban, updatedCard: KanbanCard) => {
  return {
    ...kanban,
    columns: kanban?.columns?.map((column) =>
      column._id === updatedCard.parentId
        ? updateCardInColumn(column, updatedCard)
        : column,
    ),
  } as Kanban;
};

const _upsertAutomationRule = (
  kanban: Kanban,
  rule: KanbanAutomationRule,
): Kanban => ({
  ...kanban,
  columns: kanban.columns?.map((column) => {
    const rules = column.automationRules ?? [];
    const otherRules = rules.filter(({ _id }) => _id !== rule._id);
    const hasRule = otherRules.length !== rules.length;

    if (column._id !== rule.parentId) {
      return hasRule ? { ...column, automationRules: otherRules } : column;
    }

    return {
      ...column,
      automationRules: hasRule
        ? rules.map((r) => (r._id === rule._id ? rule : r))
        : sortBy([...rules, rule], "createdAt"),
    };
  }),
});

const _removeAutomationRule = (kanban: Kanban, ruleId: string): Kanban => ({
  ...kanban,
  columns: kanban.columns?.map((column) =>
    column.automationRules?.some(({ _id }) => _id === ruleId)
      ? {
          ...column,
          automationRules: column.automationRules.filter(
            ({ _id }) => _id !== ruleId,
          ),
        }
      : column,
  ),
});

const _keepAutomationRules = (next: Kanban, previous?: Kanban): Kanban => ({
  ...next,
  columns: next.columns?.map((column) =>
    column.automationRules
      ? column
      : {
          ...column,
          automationRules: previous?.columns?.find(
            ({ _id }) => _id === column._id,
          )?.automationRules,
        },
  ),
});

interface KanbanReducer {
  selectedKanbanId: string | undefined;
  kanbansMeta: GetUserKanbansMetaResponseDto["kanbans"];
  kanban: Kanban | undefined;
  kanbanLoading: boolean;
  kanbanPendingRequestId?: string;
}

// The meta list (used by e.g. the "create deal" stage picker) carries its own
// copy of each board's columns, so a rename/reorder that only touches
// `state.kanban` would leave the meta showing stale column names. Keep the
// matching meta entry in sync with the fully-updated board.
const _syncKanbanMeta = (
  meta: KanbanReducer["kanbansMeta"],
  kanban: Kanban | undefined,
): KanbanReducer["kanbansMeta"] => {
  if (!kanban) {
    return meta;
  }

  return meta.map((m) =>
    m._id === kanban._id
      ? {
          ...m,
          name: kanban.name,
          template: kanban.template,
          columns: kanban.columns?.map((c) => ({
            _id: c._id,
            name: c.name,
            order: c.order,
            default: c.default,
          })),
        }
      : m,
  );
};

const initialState = Object.freeze<KanbanReducer>({
  selectedKanbanId: undefined,
  kanbansMeta: [],
  kanban: undefined,
  kanbanLoading: false,
  kanbanPendingRequestId: undefined,
});

/** Cards by ColumnId
 * A column and its cards
 * Used to get all columns, and if populated, each column would have its child cards
 * TODO figure out if we need this one at all or not
 */
const KanbanCardAdapter = createEntityAdapter<KanbanCardReduxEntry>({});
export const kanbanCardAdapterSelectors = KanbanCardAdapter.getSelectors();

const createKanban = AtiraThunk<void, CreateKanbanDto>(
  "/kanban/create",
  KanbanAxios.createKanban,
);

const deleteKanban = AtiraThunk<void, DeleteKanbanDto>(
  "/kanban/delete",
  KanbanAxios.deleteKanban,
);

const getKanbanCardsByKanbanColumnId = AtiraThunk<
  PageResponseDto<KanbanCard[]>,
  GetKanbanCardsByKanbanColumnIdDto
>(`/kanban/column/:id`, (dto) =>
  KanbanAxios.getKanbanCardsByKanbanColumnId(dto),
);

const createKanbanCard = AtiraThunk<void, CreateKanbanCardDto>(
  `/kanban/card/create`,
  (dto) => KanbanAxios.createKanbanCard(dto),
);

const getUserKanbansMeta = AtiraThunk<
  GetUserKanbansMetaResponseDto,
  GetUserKanbansMetaDto
>("/kanban/user/meta", KanbanAxios.getUserKanbansMeta);

const getUserKanban = AtiraThunk<Kanban, GetUserKanbanDto>(
  "/kanban/default",
  KanbanAxios.getUserKanban,
);

const getKanbanCardById = AtiraThunk<KanbanCard, GetKanbanCardByIdDto>(
  "/kanban/card/:id",
  KanbanAxios.getKanbanCardById,
);

// A card move shifts sibling `order`s, so the server still returns the whole
// board; a pure field edit returns only the updated card and the reducer
// patches it in place (see updateKanbanCard.fulfilled).
const updateKanbanCard = AtiraThunk<Kanban | KanbanCard, UpdateKanbanCardDto>(
  `/kanban/card/update`,
  KanbanAxios.updateKanbanCard,
);

// A reorder returns the whole board; a rename returns only the column.
const updateKanbanColumnById = AtiraThunk<
  Kanban | KanbanColumn,
  UpdateKanbanColumnDto
>("/kanban/column/:id", (dto) => KanbanAxios.updateKanbanColumnById(dto));

const createKanbanColumn = AtiraThunk<void, CreateKanbanColumnDto>(
  `/kanban/column/add`,
  (dto) => KanbanAxios.createKanbanColumn(dto),
);

const createAutomationRule = AtiraThunk<
  KanbanAutomationRule,
  CreateKanbanAutomationRuleDto
>("/kanban/automation/create", KanbanAxios.createAutomationRule);

const updateAutomationRule = AtiraThunk<
  KanbanAutomationRule,
  UpdateKanbanAutomationRuleDto
>("/kanban/automation/update", KanbanAxios.updateAutomationRule);

const deleteAutomationRule = AtiraThunk<void, DeleteKanbanAutomationRuleDto>(
  "/kanban/automation/delete",
  KanbanAxios.deleteAutomationRule,
);

const updateKanban = AtiraThunk<void, UpdateKanbanDto>(
  "/kanban/update",
  KanbanAxios.updateKanban,
);

const updateKanbanClosingReasons = AtiraThunk<
  void,
  UpdateKanbanClosingReasonsDto
>("/kanban/closing-reasons/update", KanbanAxios.updateKanbanClosingReasons);

const updateKanbanCardBetweenKanbans = AtiraThunk<
  void,
  UpdateKanbanCardBetweenKanbansDto
>("/kanban/card/move", KanbanAxios.updateKanbanCardBetweenKanbans);

const deleteKanbanCard = AtiraThunk<void, DeleteKanbanCardDto>(
  "/kanban/card/delete",
  KanbanAxios.deleteKanbanCard,
);

const exportKanbanCards = AtiraThunk<string, ExportKanbanCardsDto>(
  "/kanban/data/export",
  KanbanAxios.exportKanbanCards,
);

const kanbanSlice = createSlice({
  name: "kanban",
  initialState,
  reducers: {},
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getUserKanban.pending, (state, action) => {
      state.kanbanPendingRequestId = action.meta.requestId;
      state.kanbanLoading = true;
    });
    addCase(getUserKanban.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.kanbanPendingRequestId) {
        return;
      }

      state.kanbanLoading = false;
      state.selectedKanbanId = action.payload._id;
      state.kanban = action.payload;
      state.kanbanPendingRequestId = undefined;
    });
    addCase(getUserKanban.rejected, (state, action) => {
      if (action.meta.requestId !== state.kanbanPendingRequestId) {
        return;
      }

      state.kanbanLoading = false;
      state.kanbanPendingRequestId = undefined;
    });

    addCase(getUserKanbansMeta.fulfilled, (state, action) => {
      state.kanbansMeta = action.payload.kanbans;
    });

    addCase(updateKanbanCard.fulfilled, (state, action) => {
      const payload = action.payload;
      if ("title" in payload) {
        if (state.kanban) {
          state.kanban = _updateKanban(state.kanban, payload);
        }
      } else {
        state.kanban = _keepAutomationRules(payload, state.kanban);
      }
    });

    addCase(updateKanbanColumnById.fulfilled, (state, action) => {
      const payload = action.payload;
      if ("parentId" in payload) {
        if (state.kanban) {
          state.kanban = {
            ...state.kanban,
            columns: state.kanban.columns?.map((col) =>
              col._id === payload._id
                ? { ...col, name: payload.name, order: payload.order }
                : col,
            ),
          };
        }
      } else {
        state.kanban = _keepAutomationRules(payload, state.kanban);
      }

      state.kanbansMeta = _syncKanbanMeta(state.kanbansMeta, state.kanban);
    });

    addCase(createAutomationRule.fulfilled, (state, action) => {
      if (state.kanban) {
        state.kanban = _upsertAutomationRule(state.kanban, action.payload);
      }
    });

    addCase(updateAutomationRule.fulfilled, (state, action) => {
      if (state.kanban) {
        state.kanban = _upsertAutomationRule(state.kanban, action.payload);
      }
    });

    addCase(deleteAutomationRule.fulfilled, (state, action) => {
      if (state.kanban) {
        state.kanban = _removeAutomationRule(
          state.kanban,
          action.meta.arg.ruleId,
        );
      }
    });

    addCase(getKanbanCardById.fulfilled, (state, action) => {
      if (state.kanban) {
        state.kanban = _updateKanban(state.kanban, action.payload);
      }
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const kanbanActions = {
  getUserKanban,
  getUserKanbansMeta,
  getKanbanCardsByKanbanColumnId,
  getKanbanCardById,
  createKanbanCard,
  updateKanbanCard,
  updateKanbanCardBetweenKanbans,
  deleteKanbanCard,
  updateKanbanColumnById,
  createKanbanColumn,
  createAutomationRule,
  updateAutomationRule,
  deleteAutomationRule,
  updateKanban,
  updateKanbanClosingReasons,
  createKanban,
  deleteKanban,
  exportKanbanCards,
  ...kanbanSlice.actions,
};

export default kanbanSlice.reducer;
